import { IProduct } from '@/creditflowresearch/domain/vo/Product';
import { ProductType, ProductAbbreviations } from '@/creditflowresearch/domain/Product/types/ProductType';
import {
  ProductIdToTypeMap,
  ProductAbbreviationToTypeMap,
  ProductAbbreviationById,
  ProductIdByAbbreviation,
  ProductSortOrder,
} from '@/creditflowresearch/domain/Product/constants';
import Segment from '@/creditflowresearch/domain/Segment';
import Market from '@/creditflowresearch/domain/Market';
import BaseDomainEntity from '@/creditflowresearch/domain/BaseDomainEntity';

abstract class Product extends BaseDomainEntity<Product, IProduct> {
  static idRegistry = new Map<string, Product>();

  protected readonly abbreviation: ProductAbbreviations;

  protected readonly displayName: string;

  protected readonly name: string;

  protected readonly type: ProductType;

  constructor(productData: IProduct) {
    super(productData);
    this.validateProductData(productData);
    this.abbreviation = productData.abbreviation as ProductAbbreviations;
    this.displayName = productData.displayName;
    this.name = productData.name;
    this.type = ProductIdToTypeMap[productData.id];
  }

  canAccess(accessibleProductIds: string[] | Set<string>): boolean {
    if (accessibleProductIds instanceof Set) {
      return accessibleProductIds.has(this.id);
    }
    return accessibleProductIds.includes(this.id);
  }

  canAccessWith(accessibleProducts: Product[]): boolean {
    const accessibleIds = new Set(accessibleProducts.map((product) => product.getId()));
    return accessibleIds.has(this.id);
  }

  abstract getDefaultProductId(accessibleProductIds?: string[]): string;

  abstract getDefaultForContext(params: {
    accessibleProducts: Product[],
    hasAccess: boolean,
    allProductsList: IProduct[],
  }): Product | null;

  abstract getMarketIds(): string[];

  abstract getSegments(): Segment[];

  getMarkets(): Market[] {
    return this.getMarketIds()
      .map((id) => Market.from(id))
      .filter((m): m is Market => m !== null);
  }

  getSegmentIds(): string[] {
    return this.getSegments().map((s) => s.getId());
  }

  getAbbreviation(): ProductAbbreviations {
    return this.abbreviation;
  }

  getDisplayName(): string {
    return this.displayName;
  }

  getName(): string {
    return this.name;
  }

  getType(): ProductType {
    return this.type;
  }

  isOfType(type: ProductType): boolean {
    return this.type === type;
  }

  protected validateProductData(productData: IProduct): void {
    const requiredFields = [
      { field: 'id', name: 'id' },
      { field: 'abbreviation', name: 'abbreviation' },
      { field: 'displayName', name: 'displayName' },
    ];

    requiredFields.forEach(({ field, name }) => {
      if (!productData?.[field]) {
        throw new Error(`Product data must have an ${ name }`);
      }
    });
  }

  static getDefaultAbbreviation(): ProductAbbreviations {
    return ProductAbbreviations.ABS;
  }

  static getAbbreviation(productId: string): ProductAbbreviations | null {
    const abbreviation = ProductAbbreviationById[productId];
    if (!abbreviation) {
      return null;
    }
    return abbreviation as ProductAbbreviations;
  }

  static getId(abbreviation: string): string | null {
    return ProductIdByAbbreviation[abbreviation] || null;
  }

  static isOfType(identifier: string, type: ProductType): boolean {
    const typeById = ProductIdToTypeMap[identifier];
    const typeByAbbreviation = ProductAbbreviationToTypeMap[identifier];

    return typeById === type || typeByAbbreviation === type;
  }

  static areAllOfType(identifiers: string[], type: ProductType): boolean {
    if (!identifiers?.length || !type) {
      return false;
    }

    return identifiers.every((identifier) => Product.isOfType(identifier, type));
  }

  static areAllOfTypes(identifiers: string[], types: ProductType[]): boolean {
    if (!identifiers?.length || !types?.length) {
      return false;
    }

    return identifiers.every((identifier) =>
      types.some((type) => Product.isOfType(identifier, type)),
    );
  }

  static hasAnyOfType(identifiers: string[], type: ProductType): boolean {
    if (!identifiers?.length || !type) {
      return false;
    }

    return identifiers.some((identifier) => Product.isOfType(identifier, type));
  }

  static containAllTypes(identifiers: string[], types: ProductType[]): boolean {
    if (!identifiers?.length || !types?.length) {
      return false;
    }

    return types.every((type) => Product.hasAnyOfType(identifiers, type));
  }

  static getSortOrder(abbreviation: string): number | null {
    return ProductSortOrder[abbreviation as keyof typeof ProductSortOrder] ?? null;
  }
}

export default Product;
